import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Button, OverlayTrigger } from 'react-bootstrap';
import { ContentLoading, PopOver, CodeEditor } from 'axway-react-ui';
import FlowDebuggerInput from './FlowDebuggerInput';
import { createParametersManager } from './ParametersManager';

class FlowDebuggerView extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);

		// We intentionally switch off local storage and we will turn it on
		// with: https://jira.axway.com/browse/RDPP-6574
		const useCache = false;
		const {
			parametersManager,
			useAllParams,
			parameters
		} = createParametersManager(props, useCache).init();

		this.parametersManager = parametersManager;

		// doesn't change when flow changes
		// button for switching between basic and advanced modes
		this.showSwitchModeButton
			= this.parametersManager.supportsParamsOnly();

		this.state = {
			active: false,
			parameters,
			advancedModeOn: useAllParams
		};
		this.startDebug = this.startDebug.bind(this);
		this.onChangeParameters = this.onChangeParameters.bind(this);
		this.onToggle = this.onToggle.bind(this);
		this.clearDebug = this.clearDebug.bind(this);
		this.onToggleMode = this.onToggleMode.bind(this);
	}

	// Executed when switching between advanced and basic debugger mode.
	onToggleMode() {
		const { parameters, advancedModeOn } = this.state;
		this.setState({
			advancedModeOn: !advancedModeOn,
			parameters: this.parametersManager
				.toggleParameters(parameters, !advancedModeOn)
		});
	}

	// Executed when closing/opening debugger section
	onToggle() {
		this.setState({ active: !this.state.active });
	}

	onChangeParameters(parameters) {
		this.setState({
			parameters
		});
	}

	startDebug() {
		const { startDebug, flow } = this.props;
		const { parameters, advancedModeOn } = this.state;
		this.parametersManager.cache(parameters, advancedModeOn);
		startDebug(
			flow,
			JSON.parse(this.state.parameters),
			{ allParamsProvided: this.state.advancedModeOn }
		);
	}

	clearDebug() {
		const { advancedModeOn } = this.state;
		this.onChangeParameters(
			this.parametersManager.getFreshParameters(advancedModeOn)
		);
	}

	areParamsValid(parameters) {
		try {
			JSON.parse(parameters);
			return true;
		} catch (err) {
			return false;
		}
	}

	getParametersInfo(advancedModeOn) {
		if (advancedModeOn) {
			return (
				<div>
					{this.intl.formatMsg('This is an advanced input for flow parameters. The values provided can be accessed by using JSONPath selectors from within the flow.')}
					<br /><br />
					{this.intl.formatHTMLMsg('For example, if you have a flow that uses {username} and {salutation} selectors you need the following parameters:', 'fmt_advanced_parameters', { username: <i>$.params.username</i>, salutation: <i>$.config.helloworld.salutation</i> })}
					<CodeEditor
						code={JSON.stringify({
							params: {
								username: 'John'
							},
							config: {
								helloworld: {
									salutation: 'Howdy'
								}
							},
							request: {},
							env: {}
						}, null, 2)}
						mode="json"
						readOnly
						theme="eclipse"
						copyButton
						lint={false}
						lineNumbers={false}
					/>
				</div>
			);
		}

		// If we are not in advanced mode, return the info for Basic mode
		return (
			<div>
				{this.intl.formatMsg('This is a basic input for flow parameters. The values provided can be accessed by using JSONPath selectors from within the flow.')}
				<br /><br />
				{this.intl.formatHTMLMsg('For example, if you have a flow that uses {username} selector, then you need the following parameter:', 'fmt_basic_parameters', { username: <i>$.params.username</i> })}
				<CodeEditor
					code={JSON.stringify({ username: 'John' }, null, 2)}
					mode="json"
					readOnly
					copyButton
					theme="eclipse"
					lint={false}
					lineNumbers={false}
				/>
				{this.intl.formatHTMLMsg('If you wish to manually set other parameters that are automatically set in basic mode (e.g. {config} and {env}), then switch to Advanced.', { config: <i>$.config</i>, env: <i>$.env</i> })}
			</div>
		);
	}

	render() {
		const { error, loading, response, clearDebug, canDebug } = this.props;
		const { active, parameters, advancedModeOn } = this.state;
		const switchToAdvancedLabel
			= this.intl.formatMsg('Switch to Advanced');
		const switchToBasicLabel = this.intl.formatMsg('Switch to Basic');
		let view;
		if (error) {
			view = (
				<FlowDebuggerInput
					header={(
						<div className="name">{this.intl.formatMsg('Error while debugging Flow')}:
						</div>
					)}
					action={clearDebug}
					actionText={this.intl.formatMsg('Clear')}
					value={error}
					readOnly
					theme="eclipse"
					linkButton
					type="json"
				/>
			);
		} else if (loading) {
			view = <ContentLoading delay={0} />;
		} else if (response) {
			let value;
			let type;
			if (response.value === undefined) {
				type = 'text';
				value = this.intl.formatMsg('Flow successfully executed with no response');
			} else {
				value = JSON.stringify(response.value, null, '\t');
				type = 'json';
			}
			view = (
				<FlowDebuggerInput
					header={(
						<div className="name">
							{this.intl.formatMsg('Flow response')}:
						</div>
					)}
					action={clearDebug}
					actionText={this.intl.formatMsg('Clear')}
					value={value}
					readOnly
					theme="eclipse"
					linkButton
					type={type}
				/>
			);
		} else {
			// Schema overlay
			const helpPopover = (
				<PopOver
					id="flow-parameters"
					title={this.intl.formatMsg('Flow Parameters')}
				>
					{ this.getParametersInfo(advancedModeOn) }
				</PopOver>
			);

			view = (
				<FlowDebuggerInput
					header={
						<div>
							<div className="name">
								{this.intl.formatMsg('Parameters')}:
								<OverlayTrigger
									placement="right"
									trigger="click"
									rootClose
									overlay={helpPopover}
								>
									<Button className="gg icon-GG-info text" />
								</OverlayTrigger>
							</div>
							{this.showSwitchModeButton && (
								<Button
									className="cancel toggle-advanced"
									bsSize="large"
									bsStyle="link"
									onClick={this.onToggleMode}
								>
									{advancedModeOn
										? switchToBasicLabel
										: switchToAdvancedLabel}
								</Button>
							)}
						</div>
					}
					action={this.startDebug}
					secondaryAction={this.clearDebug}
					onChange={this.onChangeParameters}
					value={parameters}
					valid={canDebug && this.areParamsValid(parameters)}
					actionIcon="chevron-right-circle"
					actionText={this.intl.formatMsg('Execute Flow')}
					secondaryActionText={this.intl.formatMsg('Reset')}
					type="json"
				/>
			);
		}

		return (
			<div className="flow-debugger">
				<div className="flow-debugger-header">
					<Button
						className="action-btn"
						bsStyle="link"
						onClick={this.onToggle}
					>
						<span className="glyphicon gg icon-GG-bug" />
					</Button>
				</div>
				{active && view}
			</div>
		);
	}
}

FlowDebuggerView.propTypes = {
	flow: PropTypes.object,
	canDebug: PropTypes.bool,
	error: PropTypes.string,
	loading: PropTypes.bool,
	response: PropTypes.object,
	clearDebug: PropTypes.func,
	startDebug: PropTypes.func
};

export default injectIntl(FlowDebuggerView);
